import { createTheme, PaletteOptions } from '@mui/material/styles';

// PaletteOptionsを拡張してoriginalプロパティを追加
interface ExtendedPaletteOptions extends PaletteOptions {
  pulse: {
    info: string;
  };
}

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#d9ff00',
    },
    secondary: {
      main: '#92b304',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    pulse: {
      info: '#a1a1a1',
      text: '#cfcfcf',
    },
  } as ExtendedPaletteOptions, // as ExtendedPaletteOptionsで型を明示的に指定
  typography: {
    fontSize: 12,
    /**
     * フォントスタック。**ここが唯一の定義**（CssBaseline が body へ流すので
     * index.css 側で font-family を書かないこと。二重定義になって片方だけ直る）。
     */
    fontFamily: [
      '"Roboto"',
      '"Helvetica"',
      '"Arial"',
      // 以下は日本語。上のラテン系はCJKを持たないのでここまで落ちてくる。
      // Noto Sans JP は入れている人にだけ当たる（＝環境差が残るのを承知で置いている）。
      // ローカル導入時の名前が版によって違うので別名も並べる。
      '"Hiragino Sans"',
      '"Hiragino Kaku Gothic ProN"',
      '"Noto Sans JP"',
      '"Noto Sans CJK JP"',
      '"Source Han Sans JP"',
      'sans-serif',
    ].join(', '),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontSize: '1.4rem',
      fontWeight: 500,
      '@media (max-width:600px)': {
        fontSize: '1.1rem',
      },
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      '@media (max-width:600px)': {
        fontSize: '1rem',
      },
    },
    h6: {
      fontSize: '0.8rem',
      fontWeight: 500,
      '@media (max-width:600px)': {
        fontSize: '0.8rem',
      },
    },
    body1: {
      fontSize: '0.85rem',
      '@media (max-width:600px)': {
        fontSize: '0.8rem',
      },
    },
    body2: {
      fontSize: '0.8rem',
      '@media (max-width:600px)': {
        fontSize: '0.7rem',
      },
    },
    caption: {
      fontSize: '0.65rem',
      '@media (max-width:600px)': {
        fontSize: '0.6rem',
      },
    },
    subtitle1: {
      fontSize: '0.75rem',
      '@media (max-width:600px)': {
        fontSize: '0.7rem',
      },
    },
  },
  components: {
    /*
     * ============================================================
     * TODO（未着手・要リファクタ）: ダークモードの elevation オーバーレイ
     * ============================================================
     *
     * MUI の Paper は `variant="elevation"`（既定）かつダークモードのとき、
     * `--Paper-overlay` として白のグラデーションを `backgroundImage` に乗せている。
     * Material Design の「高い面ほど明るくして高さを表す」モデルの実装で、
     * 量は elevation から決まる（`@mui/material/styles/getOverlayAlpha`）。
     *
     *   Popover / Menu … 既定 elevation=8  → 白 11.9%
     *   Dialog         … 既定 elevation=24 → 白 16.5%
     *
     * この挙動は「面の色をテーマ既定に任せる」前提のもの。こちらは各所で
     * `rgba(0,0,0,0.65〜0.85)` を明示しているため、その上に白が乗って
     * **書いた値がそのまま描画されていない**。
     * ＝「黒を指定しているのに薄い」「不透明度を調整しても効きが悪い」の原因はこれ。
     * 現状のメニュー・ダイアログの濃さは、すべてこの白が乗った状態で調整されている。
     *
     * 【止め方】ここに
     *     MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } }
     * を足せば1箇所で止まる（影は `--Paper-shadow` の別管理なので残る。
     * `variant="outlined"` の Paper には元々乗らないので影響しない）。
     *
     * 【なぜまだやらないか】
     * 止めた瞬間に Paper 系すべて（ダイアログ・メニュー・Card・Accordion・
     * Autocomplete 等）が一段暗くなる。特にダイアログは白 16.5% が消えるので
     * 印象が大きく変わり、既存の 0.65 / 0.75 / 0.85 を実物を見ながら
     * 引き直す作業が必要になる。**対象ファイルが多いので単独の作業として切り出す。**
     * ダイアログの面をテーマへ移す作業（MuiDialog の追加）と同時にやるのが効率的。
     * ============================================================
     */
    MuiContainer: {
      styleOverrides: {
        root: {
          '&.MuiContainer-maxWidthLg': {
            maxWidth: '1120px',
          },
        },
      },
      defaultProps: {
        maxWidth: 'lg'
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          // 英語表示時に MUI 既定の全大文字化（PROFILE 等）を避ける
          textTransform: 'none',
        },
      },
    },
    /**
     * メニュー面の定義。**メニューの見た目を変えるときはここだけ触ること。**
     * マイページのバナー上の共有／…メニューは素の `<Menu>` なので、この値がそのまま出る。
     */
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
        },
      },
    },
    /**
     * `MuiMenu` と同値。**Popover は Menu の override を継承しない**ため別に書く必要がある。
     * これが無かったせいで、`Popover` で作られたメニュー（`common/PopoverMenu` と
     * その14ファイルの呼び出し側、各種ピッカー）だけが MUI Paper 既定の
     * 不透明 #1e1e1e で描かれ、バナー上のメニューと見た目が違っていた。
     * 値を分岐させないこと。分けたい理由ができたら、その理由をここに書く。
     */
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.8rem',
          '@media (max-width:600px)': {
            fontSize: '0.75rem',
            minHeight: '36px',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(25, 25, 25, 0.75)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
        },
        option: {
          fontSize: '0.8rem',
          '@media (max-width:600px)': {
            fontSize: '0.75rem',
            minHeight: '36px',
          },
        },
      },
    },
  },
});

export default theme;